import { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()
      if (error) throw error
      if (data) {
        setProfile(data)
        // Persistir onboarding_done en localStorage como respaldo
        if (data.onboarding_done) {
          localStorage.setItem('clixyads_onboarding_done', 'true')
        }
      }
      return data
    } catch (err) {
      console.error('Error fetching profile:', err)
      return null
    }
  }

  useEffect(() => {
    // Sesión actual
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user)
        await fetchProfile(session.user.id)
      }
      setLoading(false)
    })

    // Escuchar cambios de auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user)
        fetchProfile(session.user.id)
      } else {
        setUser(null)
        setProfile(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  const register = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    })
    if (error) throw error

    // Si Supabase tiene "email confirmation" activado, signUp no crea sesión
    // inmediata. Intentamos auto-login con la misma password para evitar un
    // segundo formulario; si falla, devolvemos flag para que el caller muestre
    // una pantalla "confirma tu email".
    let needsConfirmation = false
    if (!data?.session) {
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password })
      if (signInErr) needsConfirmation = true
    }
    if (data) data.needsConfirmation = needsConfirmation

    const apiUrl = import.meta.env.VITE_API_URL || ''
    if (apiUrl) {
      fetch(`${apiUrl}/api/email/welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, userId: data?.user?.id }),
      }).catch(() => {})
      try {
        const { getStoredRefCode, clearRefCode } = await import('../lib/referral')
        const refCode = getStoredRefCode()
        if (refCode && data?.user?.id) {
          fetch(`${apiUrl}/api/referrals/link-signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: data.user.id, refCode }),
          }).then(() => clearRefCode()).catch(() => {})
        }
      } catch {}
    }
    return data
  }

  const signInWithGoogle = async (redirectTo) => {
    const target = redirectTo || `${window.location.origin}/dashboard`
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: target,
        queryParams: { prompt: 'select_account' },
      },
    })
    return { data, error }
  }

  const logout = async () => {
    await supabase.auth.signOut()
    localStorage.removeItem('clixyads_onboarding_done')
    setUser(null)
    setProfile(null)
  }

  const updateProfile = async (updates) => {
    if (!user) return
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .maybeSingle()
    if (error) throw error
    if (data) setProfile(data)
    return data
  }

  // Mapear snake_case de Supabase a camelCase usado en la app
  const mergedUser = user ? {
    ...user,
    id: user.id,
    email: user.email,
    name: profile?.name || user.user_metadata?.name || user.email,
    type: profile?.type,
    city: profile?.city,
    country: profile?.country,
    bio: profile?.bio,
    phone: profile?.phone,
    web: profile?.web,
    teamSize: profile?.num_artists,
    styles: profile?.styles || [],
    process: profile?.process,
    clientType: profile?.client_type,
    clientAge: profile?.client_age,
    clientGender: profile?.client_gender,
    clientPain: profile?.client_pain,
    differentiator: profile?.differentiator,
    tone: profile?.tone,
    useEmojis: profile?.use_emojis,
    language: profile?.language,
    callToAction: profile?.call_to_action,
    instagram: profile?.instagram,
    hashtags: profile?.hashtags,
    sector: profile?.sector,
    avatarUrl: profile?.avatar_url || '',
    onboardingDone: profile?.onboarding_done === true,
    plan: profile?.plan || 'free',
    stripeCustomerId: profile?.stripe_customer_id,
    copiesUsed: profile?.copies_used || 0,
    imagesUsed: profile?.images_used || 0,
    billingPeriodStart: profile?.billing_period_start,
  } : null

  return (
    <AuthContext.Provider value={{
      user: mergedUser,
      profile,
      loading,
      login,
      register,
      signInWithGoogle,
      logout,
      updateProfile,
      fetchProfile,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
